import { getSofiaHomeThreadId } from "@/app-covi"

import {
  formatBreadcrumb,
  isOmittedBreadcrumbSegment,
} from "@/utils/breadcrumbLabels"
import { parseWorkspaceRoute } from "@/utils/workspaceRoutes"

const DASHBOARD_BASE_HREF = "/dashboard"

// Widget-config routes look like `dashboard/<dashboardId>/widgets/configure/<widgetId>`.
// These index into the dashboard-relative breadcrumb segments (the `/[slug]`
// prefix is already stripped by getBreadcrumbSegments).
const DASHBOARD_ID_SEGMENT_INDEX = 1
const WIDGETS_SEGMENT_INDEX = 2
const CONFIGURE_SEGMENT_INDEX = 3
const WIDGET_ID_SEGMENT_INDEX = 4
const WIDGETS_ROUTE_TOKEN = "widgets"
const CONFIGURE_ROUTE_TOKEN = "configure"
const NEW_WIDGET_TOKEN = "new"

export type RichBreadcrumbDescriptor =
  | {
      type: "label"
      key: string
      label: string
      href?: string
    }
  | {
      // The Sofia Home landing: renders "Sofia AI", matching the sidebar entry
      // for the surface.
      type: "sofia-brand"
      key: string
    }
  | {
      // The Sofia Home landing's "New chat" crumb: an interactive chat switcher
      // whose popover carries the new-chat action and the chat history.
      // Rendered only on the landing itself, and only while the chat rail is
      // collapsed — expanded, the rail itself is the navigation; on an open
      // chat, the full-page surface carries its own switcher.
      type: "sofia-thread-switcher"
      key: string
    }
  | {
      type: "run-title"
      key: string
      runId: string
      href?: string
      withCopy?: boolean
    }
  | {
      type: "copyable-id"
      key: string
      id: string
    }
  | {
      type: "test-set"
      key: string
      id: string
      isLast: boolean
      href?: string
    }
  | {
      type: "agent"
      key: string
      id: string
      isLast: boolean
      href?: string
    }
  | {
      type: "metric"
      key: string
      id: string
      isLast: boolean
      href?: string
    }
  | {
      type: "report"
      key: string
      id: string
      isLast: boolean
      href?: string
    }
  | {
      type: "template"
      key: string
      id: string
      isLast: boolean
      href?: string
    }
  | {
      type: "scheduled-run"
      key: string
      id: string
      isLast: boolean
      href?: string
    }

interface BuildRichBreadcrumbDescriptorsOptions {
  pathname: string
  organizationSlug?: string
  isDashboardPage: boolean
  dashboardDisplayName?: string
  getHref: (baseHref: string) => string
}

const getBreadcrumbSegments = (pathname: string, organizationSlug?: string) => {
  const parsed = parseWorkspaceRoute(pathname)
  if (parsed) {
    return parsed.relativePath.split("/").filter(Boolean)
  }

  const pathWithoutQuery = pathname.split("?")[0] ?? ""
  const segments = pathWithoutQuery.split("/").filter(Boolean)
  const routeSegments =
    segments[0] === organizationSlug ? segments.slice(1) : segments

  return routeSegments.map((segment) => segment.split("#")[0] ?? segment)
}

export const buildRichBreadcrumbDescriptors = ({
  pathname,
  organizationSlug,
  isDashboardPage,
  dashboardDisplayName,
  getHref,
}: BuildRichBreadcrumbDescriptorsOptions): RichBreadcrumbDescriptor[] => {
  if (isDashboardPage) {
    const segments = getBreadcrumbSegments(pathname, organizationSlug)
    const isWidgetConfig =
      segments[WIDGETS_SEGMENT_INDEX] === WIDGETS_ROUTE_TOKEN &&
      segments[CONFIGURE_SEGMENT_INDEX] === CONFIGURE_ROUTE_TOKEN
    const dashboardId = segments[DASHBOARD_ID_SEGMENT_INDEX]
    const widgetSegment = segments[WIDGET_ID_SEGMENT_INDEX]

    const items: RichBreadcrumbDescriptor[] = [
      {
        type: "label",
        key: "dashboard",
        label: isWidgetConfig ? "Dashboards" : "Dashboard",
        href: dashboardDisplayName ? getHref(DASHBOARD_BASE_HREF) : undefined,
      },
    ]

    if (dashboardDisplayName) {
      items.push({
        type: "label",
        key: "dashboard-name",
        label: dashboardDisplayName,
        // On the widget-config page the dashboard name links back to the
        // dashboard; on the dashboard itself it is the current page.
        href:
          isWidgetConfig && dashboardId
            ? getHref(`${DASHBOARD_BASE_HREF}/${dashboardId}`)
            : undefined,
      })
    }

    if (isWidgetConfig) {
      items.push({
        type: "label",
        key: "widget-config",
        label:
          widgetSegment === NEW_WIDGET_TOKEN ? "New Widget" : "Edit Widget",
      })
    }

    return items
  }

  const segments = getBreadcrumbSegments(pathname, organizationSlug)

  // The Sofia Home landing brands its top bar like the full-page Sofia surface,
  // then carries the chat switcher as its "New chat" crumb: "Sofia > New
  // chat ⌄". The switcher stands in for the chat rail, so the header drops it
  // again once the rail is expanded (RichDefaultPageHeader filters on that).
  if (segments.length === 1 && segments[0] === "home") {
    const items: RichBreadcrumbDescriptor[] = [
      { type: "sofia-brand", key: "sofia" },
    ]

    // A Home URL carrying a thread is the full-page chat, not the landing: it
    // overlays this header with its own switcher for the open chat. Emitting
    // the landing's null-active "New chat" switcher too would leave a second,
    // wrong switcher mounted underneath for keyboard and screen-reader users.
    if (getSofiaHomeThreadId(pathname) === null) {
      items.push({ type: "sofia-thread-switcher", key: "new-chat" })
    }

    return items
  }

  const items: RichBreadcrumbDescriptor[] = []

  segments.forEach((segment, index) => {
    if (isOmittedBreadcrumbSegment(segment, index, segments.length)) {
      return
    }

    const isLast = index === segments.length - 1
    const isRunResultRoute =
      segments[0] === "simulated" &&
      segments[1] === "runs" &&
      segments[3] === "results" &&
      segments.length >= 5

    const linkHref = (path: string) => (isLast ? undefined : getHref(path))

    if (segment === "results") {
      if (segments[0] === "uploaded") {
        items.push({
          type: "label",
          key: `${segment}-${index}`,
          label: formatBreadcrumb(segment, index),
          href: linkHref("/uploaded"),
        })
        return
      }

      if (isRunResultRoute) {
        return
      }

      if (index >= 2 && segments[index - 2] === "runs") {
        const runId = segments[index - 1]
        if (!runId) {
          return
        }
        const path = `/simulated/runs/${runId}`
        items.push({
          type: "label",
          key: `${segment}-${index}`,
          label: formatBreadcrumb(segment, index),
          href: linkHref(path),
        })
        return
      }
    }

    if (segment === "runs" && index === 1 && segments[0] === "simulated") {
      return
    }

    if (
      segments[0] === "simulated" &&
      segments[1] === "runs" &&
      segments.length === 3 &&
      index === 2
    ) {
      items.push({
        type: "run-title",
        key: `run-title-${segment}`,
        runId: segment,
        withCopy: true,
      })
      return
    }

    if (isRunResultRoute && segment === segments[2]) {
      const path = `/simulated/runs/${segment}`
      items.push({
        type: "run-title",
        key: `run-title-${segment}`,
        runId: segment,
        href: getHref(path),
      })
      return
    }

    if (isRunResultRoute && segment === segments[4]) {
      items.push({
        type: "copyable-id",
        key: `simulation-${segment}`,
        id: segment,
      })
      return
    }

    if (segment === "simulations" && index === 0) {
      items.push({
        type: "label",
        key: `${segment}-${index}`,
        label: formatBreadcrumb(segment, index),
        href: linkHref("/simulated"),
      })
      return
    }

    if (segment === "metrics") {
      items.push({
        type: "label",
        key: `${segment}-${index}`,
        label: formatBreadcrumb(segment, index),
        href: linkHref("/metrics"),
      })
      return
    }

    if (segments[index - 1] === "test-sets") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "test-set",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    if (segments[index - 1] === "agents") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "agent",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    if (segments[index - 1] === "metrics") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "metric",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    if (segments[index - 1] === "reports") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "report",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    if (segments[index - 1] === "templates") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "template",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    if (segments[index - 1] === "scheduled") {
      const path = `/${segments.slice(0, index + 1).join("/")}`
      items.push({
        type: "scheduled-run",
        key: `${segment}-${index}`,
        id: segment,
        isLast,
        href: linkHref(path),
      })
      return
    }

    const path = `/${segments.slice(0, index + 1).join("/")}`
    items.push({
      type: "label",
      key: `${segment}-${index}`,
      label: formatBreadcrumb(segment, index),
      href: linkHref(path),
    })
  })

  return items
}
